import React, { useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { TITLES, updateEvent } from '../../services/tripleCrown';
import { CAR_CLASSES, LAP_OPTIONS, TYRES, getVariablesByTitle } from '../../data/tracks';
import TrackMultiSelect from './TrackMultiSelect';
import TrackSettings, { cleanTrackSettings } from './TrackSettings';
import CarField from './CarField';
import { barbadosToUTC, tsToLocal } from '../../utils/championshipDates';
import useRules from '../../hooks/useRules';

/* ══════════════════════════════════════
   EVENT EDIT MODAL
   ══════════════════════════════════════ */
export default function EventEditModal({ event, onClose, onSaved }) {
  const availableRules = useRules();
  const variables = getVariablesByTitle(event.titleId);
  const [form, setForm] = useState({
    titleId: event.titleId || TITLES[0].id,
    phaseName: event.phaseName || '',
    description: event.description || '',
    ruleId: event.ruleId || '',
    scoringMode: event.scoringMode || 'position',
    tracks: event.tracks || [],
    laps: event.laps || '',
    carClass: event.carClass || '',
    tyre: event.tyre || '',
    trackSettings: event.trackSettings || [],
    carName: event.carName || '',
    carImage: event.carImage || '',
    variables: event.variables || [],
    startDate: tsToLocal(event.startDate) || tsToLocal(event.scheduledDate) || '',
    endDate: tsToLocal(event.endDate) || '',
    livestreamUrl: event.livestreamUrl || '',
    status: event.status || 'upcoming',
    sortOrder: event.sortOrder ?? '',
    awardsPoints: event.awardsPoints !== false,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleVariable = (varId) => {
    const current = form.variables || [];
    if (current.includes(varId)) set('variables', current.filter(v => v !== varId));
    else set('variables', [...current, varId]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const titleObj = TITLES.find(t => t.id === form.titleId);
      await updateEvent(event.id, {
        titleId: form.titleId,
        titleName: titleObj?.name || form.titleId,
        phaseName: form.phaseName.trim() || event.phaseName,
        description: form.description.trim(),
        ruleId: form.ruleId || null,
        scoringMode: form.scoringMode,
        tracks: form.tracks || [],
        track: (form.tracks || []).join(', '),
        laps: form.laps ? parseInt(form.laps) : null,
        carClass: form.titleId === 'granTurismo7' ? (form.carClass || null) : null,
        tyre: form.titleId === 'granTurismo7' ? (form.tyre || null) : null,
        trackSettings: cleanTrackSettings(form.tracks, form.trackSettings, form.titleId),
        carName: (form.carName || '').trim() || null,
        carImage: form.carImage || null,
        variables: form.variables || [],
        startDate: form.startDate ? Timestamp.fromDate(barbadosToUTC(form.startDate)) : null,
        endDate: form.endDate ? Timestamp.fromDate(barbadosToUTC(form.endDate)) : null,
        scheduledDate: form.startDate ? Timestamp.fromDate(barbadosToUTC(form.startDate)) : null,
        livestreamUrl: form.livestreamUrl.trim(),
        status: form.status,
        sortOrder: form.sortOrder !== '' ? parseInt(form.sortOrder) : null,
        awardsPoints: form.awardsPoints,
      });
      onSaved();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h2 className="modal-title">Edit Event — {event.phaseName || 'Round'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-input" rows={3} value={form.description} onChange={e => set('description', e.target.value)}
            placeholder="Brief description" style={{ resize: 'vertical' }} />
        </div>

        <div className="form-group">
          <label className="form-label">Attached Rule Set</label>
          <select className="form-select" value={form.ruleId} onChange={e => set('ruleId', e.target.value || null)}>
            <option value="">None</option>
            {availableRules.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Game Title</label>
            <select className="form-select" value={form.titleId} onChange={e => {
              set('titleId', e.target.value); set('tracks', []); set('variables', []);
            }}>
              {TITLES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Scoring Mode</label>
            <select className="form-select" value={form.scoringMode} onChange={e => set('scoringMode', e.target.value)}>
              <option value="time">Time-Based</option>
              <option value="position">Position-Based</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Round Name</label>
            <input className="form-input" value={form.phaseName} onChange={e => set('phaseName', e.target.value)} />
          </div>
          <div className="form-group" style={{ maxWidth: 120 }}>
            <label className="form-label">Laps</label>
            <select className="form-select" value={form.laps || ''} onChange={e => set('laps', e.target.value ? parseInt(e.target.value) : null)}>
              <option value="">N/A</option>
              {LAP_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          {form.titleId === 'granTurismo7' && (
            <div className="form-group" style={{ maxWidth: 160 }}>
              <label className="form-label">Car Class</label>
              <select className="form-select" value={form.carClass || ''} onChange={e => set('carClass', e.target.value)}>
              <option value="">Any / not set</option>
              {CAR_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            </div>
          )}
          {form.titleId === 'granTurismo7' && (
            <div className="form-group" style={{ maxWidth: 170 }}>
              <label className="form-label">Tyres</label>
              <select className="form-select" value={form.tyre || ''} onChange={e => set('tyre', e.target.value)}>
                <option value="">Any / not set</option>
                {TYRES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          <div className="form-group" style={{ maxWidth: 100 }}>
            <label className="form-label">Sort Order</label>
            <input className="form-input" type="number" value={form.sortOrder} onChange={e => set('sortOrder', e.target.value)} placeholder="1, 2…" />
          </div>
          <div className="form-group" style={{ maxWidth: 140 }}>
            <label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="upcoming">Upcoming</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={form.awardsPoints} onChange={e => set('awardsPoints', e.target.checked)} />
            <span style={{ fontWeight: 600 }}>Awards Points</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
              {form.awardsPoints ? '— Points calculated from positions' : '— Position only, no points awarded'}
            </span>
          </label>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Start Date & Time</label>
            <input className="form-input" type="datetime-local" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">End Date & Time</label>
            <input className="form-input" type="datetime-local" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Tracks</label>
          <TrackMultiSelect titleId={form.titleId} selected={form.tracks || []} onChange={tracks => set('tracks', tracks)} />
          <TrackSettings titleId={form.titleId} tracks={form.tracks || []} settings={form.trackSettings}
            onChange={v => set('trackSettings', v)} defaultLaps={form.laps} defaultCarClass={form.carClass} defaultTyre={form.tyre} />
        </div>

        <CarField name={form.carName} image={form.carImage} uploadKey={`edit-${event.id}`}
          onName={v => set('carName', v)} onImage={v => set('carImage', v)} />

        {variables.length > 0 && (
          <div className="form-group">
            <label className="form-label">Race Variables</label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {variables.map(v => (
                <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={(form.variables || []).includes(v.id)} onChange={() => toggleVariable(v.id)} />
                  {v.label}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Livestream URL</label>
          <input className="form-input" value={form.livestreamUrl} onChange={e => set('livestreamUrl', e.target.value)} placeholder="YouTube / Twitch embed URL" />
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Update Event'}
          </button>
        </div>
      </div>
    </div>
  );
}
